import { BlockedCreatorsFlow, NotificationType, useBlockedCreators, useTranslation, useUserSettings } from "@next-platform/client-common-core";
import { FetchStatus } from "@next-platform/client-common-core/user/userSettings/types";
import { useMedia } from "@next-platform/client-common-spicy-ui";
import { Card, CardBody } from "@nextui-org/react";
import { useCallback, useEffect, useState } from "react";

import { PageLayout, PageSize } from "@/components";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import { Row, Text } from "@/components/customComponents";
import ErrorSection from "@/components/ErrorSection/ErrorSection";
import Trans from "@/components/Trans/Trans";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";

import BlockedCreatorsEmptyState from "./components/BlockedCreatorsEmptyState";
import BlockedCreatorsList from "./components/BlockedCreatorsList";

const BlockedCreators = () => {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const { onUserUnblockCreator, isLoading: isBlockLoading, areUserSettingsLoaded, areUserSettingLoading } = useUserSettings();
  const [stateSelectedCreatorId, setStateSelectedCreatorId] = useState("");
  const [stateSelectedCreatorUsername, setStateSelectedCreatorUsername] = useState("");
  const [stateIsConfirmUnblockModalVisible, setStateIsConfirmUnblockModalVisible] = useState(false);

  const { isMobile } = useMedia();
  const { isAuthenticated } = useAuth();
  const hasUserSettingsApiFailed = areUserSettingLoading === FetchStatus.failed;
  const isUserSettingsPending = isAuthenticated && !areUserSettingsLoaded;

  const {
    blockedCreators,
    hasMoreBlockedCreators,
    hasInitialLoadCompleted,
    errorType,
    errorMessage,
    isLoading,
    loadBlockedCreators,
    loadMoreBlockedCreators,
    removeBlockedCreator,
    retryInitialLoad,
    resetError,
  } = useBlockedCreators();

  const hasInitialLoadFailed = errorType === BlockedCreatorsFlow.LOAD_BLOCKED_CREATORS && blockedCreators.length === 0;
  const isInitialLoading = isAuthenticated && areUserSettingsLoaded && !hasInitialLoadCompleted && !hasInitialLoadFailed;
  const hasBlockedCreators = blockedCreators.length > 0;
  const shouldShowList = hasBlockedCreators || hasMoreBlockedCreators;

  useEffect(() => {
    if (isAuthenticated && areUserSettingsLoaded && !hasInitialLoadCompleted) {
      void loadBlockedCreators();
    }
  }, [areUserSettingsLoaded, hasInitialLoadCompleted, isAuthenticated, loadBlockedCreators]);

  useEffect(() => {
    if (errorType === BlockedCreatorsFlow.LOAD_MORE_BLOCKED_CREATORS && errorMessage) {
      displayToast({ message: errorMessage, type: NotificationType.error });
      resetError();
    }
  }, [displayToast, errorMessage, errorType, resetError]);

  const unblockSelectedCreator = useCallback(async () => {
    await onUserUnblockCreator(stateSelectedCreatorId);
    removeBlockedCreator(stateSelectedCreatorId);
  }, [onUserUnblockCreator, removeBlockedCreator, stateSelectedCreatorId]);

  const handleUnblockButtonPress = useCallback(({ creatorId, username }: { creatorId: string; username: string }) => {
    setStateSelectedCreatorId(creatorId);
    setStateSelectedCreatorUsername(username);
    setStateIsConfirmUnblockModalVisible(true);
  }, []);

  const handleUnblockModalClose = useCallback(() => {
    setStateSelectedCreatorId("");
    setStateIsConfirmUnblockModalVisible(false);
  }, []);

  const handleLoadMorePress = useCallback(() => {
    void loadMoreBlockedCreators();
  }, [loadMoreBlockedCreators]);

  const handleRetryInitialLoadPress = useCallback(() => {
    void retryInitialLoad();
  }, [retryInitialLoad]);

  return (
    <PageLayout size={PageSize.Large} className="flex grow">
      <div className="flex flex-col grow" data-testid="BlockedCreators">
        <PageStaticMetaTags pageId="blockedCreators" />
        <Row className="pl-4">
          <Text h3>
            <Trans i18nKey="creator:page.blocked.title" />
          </Text>
        </Row>
        {hasUserSettingsApiFailed || hasInitialLoadFailed ? (
          <ErrorSection shouldCenter={true} onClick={hasInitialLoadFailed ? handleRetryInitialLoadPress : undefined} />
        ) : (
          <div className="flex flex-row">
            <Card className="bg-transparent flex text-center w-full m-2.5 border-1 border-solid border-slate-200 dark:border-slate-800">
              <CardBody className={`py-0 ${isMobile ? "pl-[15px] pr-[10px]" : "px-[30px]"}`}>
                {(() => {
                  if (isUserSettingsPending) {
                    return <BlockedCreatorsEmptyState isAuthenticated={isAuthenticated} isInitialLoading={true} />;
                  }

                  if (shouldShowList) {
                    return (
                      <BlockedCreatorsList
                        blockedCreators={blockedCreators}
                        hasMoreBlockedCreators={hasMoreBlockedCreators}
                        isLoading={isLoading}
                        onLoadMorePress={handleLoadMorePress}
                        onUnblockPress={handleUnblockButtonPress}
                      />
                    );
                  }

                  return <BlockedCreatorsEmptyState isAuthenticated={isAuthenticated} isInitialLoading={isInitialLoading} />;
                })()}
              </CardBody>
            </Card>
          </div>
        )}
      </div>
      <ConfirmationModal
        heading={t("creator:modal.unblock.title")}
        isVisible={stateIsConfirmUnblockModalVisible}
        confirmationMessage={t("creator:modal.unblock.description", { username: stateSelectedCreatorUsername })}
        handleClose={handleUnblockModalClose}
        onUserConfirmation={unblockSelectedCreator}
        isLoading={isBlockLoading}
        actionButtonText={t("common:yes")}
        discardButtonText={t("common:no")}
      />
    </PageLayout>
  );
};

export default BlockedCreators;
